import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  AppNotification,
  DashboardPreferences,
  DateRange,
  ThemeMode,
  User,
  UserRole,
  WidgetId,
  Workspace,
} from "@/types";
import { defaultDashboardPreferences } from "@/services/mock/dashboardPreferences";
import { mockWorkspace } from "@/services/mock/user";
import { notificationService } from "@/services/notificationService";
import {
  changeEmailRequest,
  changePasswordRequest,
  fetchSession,
  loginRequest,
  logoutRequest,
  type AuthUser,
} from "@/modules/auth/api/authApi";
import { revokeOperaDeviceTokenOnLogout } from "@/native/operaPushNotifications";

interface AuthSlice {
  isAuthenticated: boolean;
  authReady: boolean;
  user: User | null;
  setupRequired: boolean;
  bootstrapAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  changePassword: (input: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => Promise<{ ok: boolean; error?: string; requiresReauth?: boolean }>;
  changeEmail: (input: {
    currentPassword: string;
    newEmail: string;
    confirmEmail: string;
  }) => Promise<{ ok: boolean; error?: string; requiresReauth?: boolean }>;
  updateUser: (partial: Partial<User>) => void;
}

interface ThemeSlice {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

interface WorkspaceSlice {
  workspace: Workspace;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
}

interface NotificationSlice {
  notifications: AppNotification[];
  notificationsLoading: boolean;
  loadNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  filter: "all" | "unread";
  setFilter: (filter: "all" | "unread") => void;
}

interface PreferencesSlice {
  preferences: DashboardPreferences;
  setWidgetVisible: (id: WidgetId, visible: boolean) => void;
  reorderWidgets: (orderedIds: WidgetId[]) => void;
  resetPreferences: () => void;
}

export type AppStore = AuthSlice &
  ThemeSlice &
  WorkspaceSlice &
  NotificationSlice &
  PreferencesSlice;

const defaultRange: DateRange = {
  from: "2026-07-01",
  to: "2026-07-19",
  label: "Jul 1 – Jul 19, 2026",
};

function mapRole(role: string): UserRole {
  const r = role.toLowerCase();
  if (r === "ceo") return "CEO";
  if (r === "admin" || r === "administrator") return "Admin";
  if (r === "manager") return "Manager";
  return "Staff";
}

function toUser(authUser: AuthUser): User {
  return {
    id: authUser.id,
    email: authUser.email,
    name: authUser.name,
    role: mapRole(authUser.role),
    initials: authUser.initials || "OA",
    avatarUrl: authUser.avatarUrl || undefined,
  };
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      authReady: false,
      user: null,
      setupRequired: false,
      bootstrapAuth: async () => {
        try {
          const session = await fetchSession();
          if (session.authenticated && session.user) {
            set({
              isAuthenticated: true,
              user: toUser(session.user),
              setupRequired: false,
              authReady: true,
            });
            void get().loadNotifications();
          } else {
            set({
              isAuthenticated: false,
              user: null,
              setupRequired: Boolean(session.setupRequired),
              authReady: true,
              notifications: [],
            });
          }
        } catch {
          // Transient network/API failure must not invent a "logged out" state.
          // Leave prior UI flags untouched; mark ready so guards can resolve.
          set({ authReady: true });
        }
      },
      login: async (email, password) => {
        const trimmed = email.trim();
        if (!trimmed || password.length < 1) {
          return { ok: false, error: "Email and password are required." };
        }
        try {
          const result = await loginRequest(trimmed, password);
          if (!result.ok || !result.user) {
            return { ok: false, error: result.error || "Invalid credentials." };
          }
          set({
            isAuthenticated: true,
            user: toUser(result.user),
            setupRequired: false,
          });
          void get().loadNotifications();
          return { ok: true };
        } catch {
          return { ok: false, error: "Sign-in failed. Please try again." };
        }
      },
      logout: async () => {
        // Soft-revoke FCM token while session/CSRF still valid — never clears cookies/storage.
        try {
          await revokeOperaDeviceTokenOnLogout();
        } catch {
          // Non-fatal: logout must continue even if push revoke fails.
        }
        await logoutRequest();
        set({ isAuthenticated: false, user: null, notifications: [] });
      },
      changePassword: async (input) => {
        const result = await changePasswordRequest(input);
        if (result.ok && result.requiresReauth) {
          set({ isAuthenticated: false, user: null });
        }
        return result;
      },
      changeEmail: async (input) => {
        const result = await changeEmailRequest(input);
        if (result.ok && result.requiresReauth) {
          set({ isAuthenticated: false, user: null });
        }
        return result;
      },
      updateUser: (partial) => {
        const current = get().user;
        if (!current) return;
        // Login email is owned by session auth APIs only — never patch from profile/mocks.
        const { email: _ignoredEmail, ...safe } = partial;
        set({ user: { ...current, ...safe } });
      },

      theme: "light",
      setTheme: (theme) => {
        const resolved =
          theme === "system"
            ? window.matchMedia("(prefers-color-scheme: dark)").matches
              ? "dark"
              : "light"
            : theme;
        document.documentElement.setAttribute("data-theme", resolved);
        set({ theme });
      },
      toggleTheme: () => {
        const current = get().theme;
        const resolved =
          current === "system"
            ? window.matchMedia("(prefers-color-scheme: dark)").matches
              ? "dark"
              : "light"
            : current;
        get().setTheme(resolved === "light" ? "dark" : "light");
      },

      workspace: mockWorkspace,
      dateRange: defaultRange,
      setDateRange: (dateRange) => set({ dateRange }),

      notifications: [],
      notificationsLoading: false,
      filter: "all",
      setFilter: (filter) => set({ filter }),
      loadNotifications: async () => {
        if (!get().isAuthenticated) {
          set({ notifications: [], notificationsLoading: false });
          return;
        }
        set({ notificationsLoading: true });
        try {
          const { notifications } = await notificationService.list("all");
          set({ notifications, notificationsLoading: false });
        } catch {
          set({ notificationsLoading: false });
        }
      },
      markAsRead: async (id) => {
        set({
          notifications: get().notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        });
        try {
          await notificationService.markRead(id);
        } catch {
          void get().loadNotifications();
        }
      },
      markAllRead: async () => {
        set({
          notifications: get().notifications.map((n) => ({ ...n, read: true })),
        });
        try {
          await notificationService.markAllRead();
        } catch {
          void get().loadNotifications();
        }
      },

      preferences: defaultDashboardPreferences,
      setWidgetVisible: (id, visible) =>
        set({
          preferences: {
            ...get().preferences,
            widgets: get().preferences.widgets.map((w) =>
              w.id === id ? { ...w, visible } : w
            ),
          },
        }),
      reorderWidgets: (orderedIds) =>
        set({
          preferences: {
            ...get().preferences,
            widgets: orderedIds.map((id, order) => {
              const existing = get().preferences.widgets.find((w) => w.id === id)!;
              return { ...existing, order };
            }),
          },
        }),
      resetPreferences: () => set({ preferences: defaultDashboardPreferences }),
    }),
    {
      name: "cyberweb-opera-os",
      partialize: (state) => ({
        theme: state.theme,
        preferences: state.preferences,
        dateRange: state.dateRange,
      }),
    }
  )
);
